import { atom } from 'recoil'
import { getNewRefreshTime, getTokenExpiration } from '../lib/token'
import { axios } from '../lib/request'
import { ETERNAL_REFRESH_TOKEN } from '../consts/auth'
import { fetchUser } from '../monitor/User.state'

/**
 * Create custom sign in state to be used as AuthProvider key.
 * With the key, provider re-renders when user is authenticated and
 * triggers an updated interval for the useRefreshApi hook.
 */
export const signInState = atom({
    key: 'sign-in-key',
    default: null,
})

const buildWhitelabelUrl = (url) => (
    url?.replace("WHITELABEL", process.env.REACT_APP_WHITELABEL)
)

export const signInWithGoogle = (idToken) => axios.post(
    buildWhitelabelUrl(process.env.REACT_APP_API_GOOGLE_AUTH_URL),
    { id_token: idToken },
)

export const signInWithApple = ({ code, state }) => {
    const body = new URLSearchParams()
    body.append("code", code)
    if (state) {
        body.append("state", state)
    }

    return axios.post(
        buildWhitelabelUrl(process.env.REACT_APP_API_APPLE_CALLBACK_URL),
        body,
        {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        },
    )
}

export const buildSignInToken = (data, user_id) => {
    try {
        const { lifespan, refresh_token, token } = data
        const tokenType = "Bearer"
        const authState = {
            user_id,
            user_type: "email",
            refresh_token,
            token,
            lifespan,
            token_received_at: new Date().toLocaleString(),
            token_expired_at: new Date(getNewRefreshTime(lifespan)).toLocaleString(),
        }

        return {
            authState,
            token,
            tokenType,
            expiresIn: getTokenExpiration(token),
            refreshToken: refresh_token,
            refreshTokenExpireIn: getTokenExpiration(refresh_token),
        }
    } catch {
        return {}
    }
}

export const refreshToken = async (
    {
        refreshToken: defaultRefreshToken,
        isRefresh,
        currentAuthState = null,
    }) => {
    try {
        let userId = currentAuthState?.user_id
        const refreshToken = localStorage.getItem(`${ETERNAL_REFRESH_TOKEN}`) || defaultRefreshToken
        console.debug(`REFTOKEN: Last token received at ${currentAuthState?.token_received_at}`)
        console.debug(`REFTOKEN: Last token expired at ${currentAuthState?.token_expired_at}`)
        console.debug(`REFTOKEN: Refreshing with: -- ${(refreshToken || '')} at ${new Date()}`)
        const response = await axios.post(process.env.REACT_APP_API_REFRESH_URL,
            { 
                // Attempt to retrieve eternal refresh token from local storage for reliability;
                // it's always there and is detached from the react-auth-kit lib.
                'refresh_token': refreshToken 
            },
        )
        if (response?.data) {
            const { token, refresh_token, ...data } = response.data

            // Eternally store refresh token
            localStorage.setItem(ETERNAL_REFRESH_TOKEN, refresh_token)

            // user id could be unset when the user is not authenticated (token is expired)
            // in this case, we need to fetch the user data to get the user id
            if (!userId && token) {
                const user = await fetchUser(undefined, {
                    Authorization: `Bearer ${token}`,
                })
                userId = user?.email
            }
                                
            if (isRefresh) {
                console.debug(`REFTOKEN: Refreshed from lib ${(token || '').slice(-10)} at ${new Date()}`)
                const newAuthTokenExpireIn = getTokenExpiration(token)
                return {
                    isSuccess: true,
                    newAuthUserState: { 
                        ...currentAuthState, 
                        user_id: userId,
                        token,
                        refresh_token,
                        // Add additional debug information to the user state.
                        // For debugging purposes, newAuthTokenExpireIn can be rounded up for readability.
                        token_received_at: new Date().toLocaleString(),
                        token_expired_at: new Date(getNewRefreshTime(Math.round(newAuthTokenExpireIn))).toLocaleString(),
                    },
                    newAuthToken: token,
                    newAuthTokenExpireIn,
                    newRefreshToken: refresh_token,
                    newRefreshTokenExpiresIn: getTokenExpiration(refresh_token),
                }
            }
            return buildSignInToken({ token, refresh_token, ...data }, userId)
        } else {
            console.debug(`REFTOKEN: refresh token failed. No data from server. Server response: ${JSON.stringify(response)}`)
        }
        return {
            isSuccess: false,
        }
    } catch (e) {
        console.debug(`REFTOKEN: refresh token failed. Error: ${e}`)

        return {
            isSuccess: false,
        }
    } finally {
        document.dispatchEvent(new Event("tokenRefresh"))
    }
}
